import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { JournalPageView } from './JournalPageView';
import { useJournalPage } from './useJournalPage';

type JournalPageProps = {
    /** Open the New-entry modal on mount (deep link `/accounting/journal/new`). */
    initialNewOpen?: boolean;
};

/**
 * Journal list page: a paged, filterable table of entries. "New entry" opens
 * the entry form as a modal; a row opens the detail drawer. Arriving with
 * `?account=<id>` (Chart-of-Accounts drill-in) scopes the list to that account
 * and auto-opens the first match.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, since React does not catch a component's own throws (the
 * repo's self-served page pattern).
 */
export const JournalPage: React.FC<JournalPageProps> = ({
    initialNewOpen = false,
}) => {
    const model = useJournalPage({ initialNewOpen });
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('journal.error.title'),
            errorMessage: t('journal.error.message'),
            errorRetry: t('journal.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('journal-page')}
        >
            <ErrorBoundary
                data-testid="journal-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="journal-error-boundary"
                onRetry={model.onRetry}
                resetKeys={[model.loadError]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <JournalPageContent model={model} />
            </ErrorBoundary>
        </div>
    );
};

type JournalPageContentProps = {
    model: ReturnType<typeof useJournalPage>;
};

const JournalPageContent: React.FC<JournalPageContentProps> = ({ model }) => {
    if (model.loadError) {
        throw model.loadError;
    }

    return <JournalPageView model={model} />;
};
